import { AND } from '~/common/constants/openSearch';
import { Filter } from '~/evifields/src/types/Filter';
import { SearchFilter } from '~/types/DashboardV2';
import {
  BooleanQueryEntity,
  FilterQueryEntity,
  QueryOperatorEntity,
  SectionEntity,
} from '~/types/OpenSearch';

import { BuildQueryParams, SearchFilterRecord, SearchQuery } from '../types';
import { toEntity } from './filters';

export const testIsFilterEmpty = (filter: Filter) => !filter.operatorId;

export const buildComplexQueryWithCrossFilters = (
  queryBuilderData: SearchQuery,
  initialCrossFilters: Filter[],
  searchFilters: Record<string, SearchFilter>,
  booleanQuery?: string,
): SearchQuery => {
  let query: SearchQuery = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  let crossFilters = initialCrossFilters;
  if (searchFilters) {
    crossFilters = attachFilterType(crossFilters, searchFilters);
  }

  if (queryBuilderData.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }
    if (queryBuilderData.length) {
      query = query.concat(queryBuilderData);
      query.push(AND);
    }

    crossFilters.forEach((crossFilter) => {
      if (crossFilter.fieldType) {
        query.push(toEntity(crossFilter, crossFilter.fieldType));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

export const buildQuery = (payload?: BuildQueryParams): SearchQuery => {
  if (!payload) {
    return [];
  }

  const {
    booleanQuery,
    crossFilters = [],
    filters = [],
    searchFilters = {},
  } = payload;

  const query:
    | SectionEntity[]
    | Array<
        FilterQueryEntity<any, any> | BooleanQueryEntity | QueryOperatorEntity
      > = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  if (filters.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }

    filters.forEach((filter) => {
      const searchFilter = searchFilters[filter.fieldId];
      if (searchFilter) {
        query.push(toEntity(filter, searchFilter.type));
        query.push(AND);
      }
    });

    crossFilters.forEach((crossFilter) => {
      const searchFilter = searchFilters[crossFilter.fieldId];
      if (searchFilter) {
        query.push(toEntity(crossFilter, searchFilter.type));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

// TODO: Will potentially replace buildQuery once we get it working with query builder and cross filter as well.
export const buildFilterQuery = (payload?: BuildQueryParams): any => {
  if (!payload) {
    return [];
  }

  const {
    booleanQuery,
    crossFilters: initialCrossFilters = [],
    filters: initialFilters = [],
    searchFilters,
  } = payload;

  const query:
    | SectionEntity[]
    | Array<
        FilterQueryEntity<any, any> | BooleanQueryEntity | QueryOperatorEntity
      > = booleanQuery
    ? [{ type: 'bool_text_search', value: booleanQuery }]
    : [];

  let filters = initialFilters;
  let crossFilters = initialCrossFilters;
  if (searchFilters) {
    filters = attachFilterType(filters, searchFilters);
    crossFilters = attachFilterType(crossFilters, searchFilters);
  }

  if (filters.length || crossFilters.length) {
    if (booleanQuery) {
      query.push(AND);
    }

    filters.forEach((filter) => {
      if (filter.fieldType) {
        query.push(toEntity(filter, filter.fieldType));
        query.push(AND);
      }
    });

    crossFilters.forEach((crossFilter) => {
      if (crossFilter.fieldType) {
        query.push(toEntity(crossFilter, crossFilter.fieldType));
        query.push(AND);
      }
    });

    query.pop();
  }

  return query;
};

export const attachFilterType = (
  filters: Filter[],
  searchFilters: SearchFilterRecord = {},
) =>
  filters.map((filter) => {
    const searchFilter = searchFilters[filter.fieldId];
    const updatedFilter = { ...filter };
    if (searchFilter) {
      updatedFilter.fieldType = searchFilter.type;
    }
    return updatedFilter;
  });
